import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  FileDownload as ExportIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  People,
  Security,
  Category,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Category as CategoryType, apiService } from '../../services/apiService';
import SettingsSidebar from '../../components/Common/SettingsSidebar';

const CategoriesManagement: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<CategoryType | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<CategoryType>>({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiService.getCategories();
      setCategories(response.data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, category: CategoryType) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategory(category);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = (category?: CategoryType) => {
    setAnchorEl(null);
    const categoryToDelete = category || selectedCategory;
    if (categoryToDelete) {
      Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await apiService.deleteCategory(categoryToDelete.id);
            toast.success('Category deleted successfully');
            fetchData();
          } catch (error: any) {
            toast.error(error.message || 'Failed to delete category');
          }
        }
      });
    }
  };

  const handleEditClick = (category?: CategoryType) => {
    const categoryToEdit = category || selectedCategory;
    if (categoryToEdit) {
      setEditingCategoryId(categoryToEdit.id);
      setFormData(categoryToEdit);
      setIsEditing(true);
      setDialogOpen(true);
      setAnchorEl(null);
    }
  };

  const handleAddNew = () => {
    setFormData({
      name: '',
      description: '',
      color: '#4A90E2',
      status: 'active',
      notes: '',
    });
    setIsEditing(false);
    setDialogOpen(true);
  };

  const handleFormChange = (field: keyof CategoryType, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (isEditing && editingCategoryId) {
        await apiService.updateCategory(editingCategoryId, formData);
        toast.success('Category updated successfully');
      } else {
        await apiService.createCategory(formData);
        toast.success('Category created successfully');
      }
      
      setDialogOpen(false);
      setEditingCategoryId(null);
      fetchData();
    } catch (error: any) {
      toast.error(error.message || `Failed to ${isEditing ? 'update' : 'create'} category`);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingCategoryId(null);
    setFormData({});
  };

  const filteredCategories = categories.filter(category =>
    category.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedCategories = filteredCategories.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', fontSize: '2rem' }}>
          Stock Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
          sx={{ 
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: '#4A90E2',
            color: 'white',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#3b82f6',
            },
          }}
        >
          New Category
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Sidebar - Stock Menu */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SettingsSidebar 
            variant="stock" 
            currentView="categories"
            onViewChange={(view: any) => {
              if (view === 'items' || view === 'stocks') {
                navigate('/stock');
              }
            }}
          />
        </Grid>

        {/* Right Content Area */}
        <Grid size={{ xs: 12, md: 9 }}>
          {/* Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                        Total Categories
                      </Typography>
                      <Typography variant="h4" sx={{ color: '#1f2937', fontWeight: 'bold' }}>
                        {categories.length}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '12px', 
                      backgroundColor: '#e0f2f1', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Typography variant="h6" sx={{ color: '#0d9488', fontWeight: 'bold' }}>
                        {categories.length}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                        Active Categories
                      </Typography>
                      <Typography variant="h4" sx={{ color: '#1f2937', fontWeight: 'bold' }}>
                        {categories.filter(cat => cat.status === 'active').length}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '12px', 
                      backgroundColor: '#e0f2f1', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Typography variant="h6" sx={{ color: '#0d9488', fontWeight: 'bold' }}>
                        {categories.filter(cat => cat.status === 'active').length}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                        New Categories (This Month)
                      </Typography>
                      <Typography variant="h4" sx={{ color: '#1f2937', fontWeight: 'bold' }}>
                        {categories.filter(cat => {
                          const createdDate = new Date(cat.created_at);
                          const now = new Date();
                          return createdDate.getMonth() === now.getMonth() && 
                                 createdDate.getFullYear() === now.getFullYear();
                        }).length}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '12px', 
                      backgroundColor: '#e0f2f1', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Typography variant="h6" sx={{ color: '#0d9488', fontWeight: 'bold' }}>
                        {categories.filter(cat => {
                          const createdDate = new Date(cat.created_at);
                          const now = new Date();
                          return createdDate.getMonth() === now.getMonth() && 
                                 createdDate.getFullYear() === now.getFullYear();
                        }).length}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            border: 'none'
          }}>
            <CardContent sx={{ p: 3 }}>
          {/* Search and Action Bar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
            <TextField
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#f8fafc',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #e2e8f0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #cbd5e1',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: '2px solid #4A90E2',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#6b7280' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 120, height: '48px' }}>
              <InputLabel>Filter</InputLabel>
              <Select
                label="Filter"
                sx={{ 
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#f8fafc',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #e2e8f0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #cbd5e1',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: '2px solid #4A90E2',
                  },
                }}
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              sx={{
                height: '48px',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 500,
                borderColor: '#e2e8f0',
                color: '#64748b',
                px: 3,
                '&:hover': {
                  borderColor: '#cbd5e1',
                  backgroundColor: '#f8fafc',
                },
              }}
            >
              Export
            </Button>
          </Box>

              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e2e8f0', borderRadius: '12px', height: 'calc(100vh - 450px)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell padding="checkbox">
                    <Checkbox />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Display Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Color</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Loading categories...
                    </TableCell>
                  </TableRow>
                ) : paginatedCategories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No categories found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCategories.map((category) => (
                    <TableRow key={category.id} hover sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                      <TableCell padding="checkbox">
                        <Checkbox />
                      </TableCell>
                      <TableCell sx={{ color: '#475569' }}>{category.name}</TableCell>
                      <TableCell sx={{ color: '#475569' }}>{category.display_name || category.name}</TableCell>
                      <TableCell sx={{ color: '#475569' }}>{category.description || 'N/A'}</TableCell>
                      <TableCell sx={{ color: '#475569' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '4px',
                              backgroundColor: category.color || '#4A90E2',
                            }}
                          />
                          <Typography variant="body2">{category.color || '#4A90E2'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={category.status}
                          size="small"
                          color={getStatusColor(category.status) as any}
                          sx={{
                            backgroundColor: category.status === 'active' ? '#e0f2f1' : '#fee2e2',
                            color: category.status === 'active' ? '#0d9488' : '#dc2626',
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleEditClick(category)}
                            sx={{
                              backgroundColor: '#e0f2f1',
                              color: '#0d9488',
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontWeight: 500,
                              px: 2,
                              py: 1,
                              '&:hover': {
                                backgroundColor: '#ccedea',
                              },
                            }}
                          >
                            Update
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleDeleteClick(category)}
                            sx={{
                              backgroundColor: '#fee2e2',
                              color: '#dc2626',
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontWeight: 500,
                              px: 2,
                              py: 1,
                              '&:hover': {
                                backgroundColor: '#fecaca',
                              },
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredCategories.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              borderTop: '1px solid #e5e7eb',
              '& .MuiTablePagination-toolbar': {
                paddingLeft: 0,
                paddingRight: 0,
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {isEditing ? 'Update Category' : 'Create New Category'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <TextField
              label="Category Name"
              value={formData.name || ''}
              onChange={(e) => handleFormChange('name', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description || ''}
              onChange={(e) => handleFormChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Color"
                value={formData.color || '#4A90E2'}
                onChange={(e) => handleFormChange('color', e.target.value)}
                sx={{ flex: 1 }}
                type="color"
              />
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status || 'active'}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Notes"
              value={formData.notes || ''}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              backgroundColor: '#10b981',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#059669',
              },
            }}
          >
            {isEditing ? 'Update Category' : 'Create Category'}
          </Button>
        </DialogActions>
      </Dialog>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CategoriesManagement;
